import { TRPCClientErrorLike } from "@trpc/client";
import { useRouter } from "next/router";
import { UrlObject } from "url";

import type { AppRouter } from "~/server/api/root";

import { isUuid } from "~/utils/schemas";

const UUID_PATH_PARAM_REGEX_STRING = "([0-9a-zA-z-]+)";
const PATH_END_REGEX_STRING = "(?:\\/)?(\\?([^\\/]+)?(\\/)?)?$";

/**
 * Creates a regular expression that can be used to match a path with or without one or multiple UUID path parameters and one or multiple
 * query parameters.
 *
 * @example
 * // This regex will match all of the following:
 * // 1. "customers/407e42c3-4b3d-4f53-9263-4d5d3a4cd35a"
 * // 2. "customers/407e42c3-4b3d-4f53-9263-4d5d3a4cd35a/"
 * // 3. "/customers/407e42c3-4b3d-4f53-9263-4d5d3a4cd35a?name=X"
 * // 4. "/customers/407e42c3-4b3d-4f53-9263-4d5d3a4cd35a?name=X/"
 * const regex = createLeadingPathRegex("/customers/:id/");
 */
export const createLeadingPathRegex = (path: string, options?: { wildcardPathEnd?: boolean }): RegExp => {
  path = path.startsWith("/") ? path.substring(1) : path;
  path = path.endsWith("/") ? path.substring(0, path.length - 1) : path;
  const regexBase = `^(/\\[orgSlug\\])?/${path.replaceAll(":id", UUID_PATH_PARAM_REGEX_STRING)}`;
  if (options?.wildcardPathEnd === true) {
    return new RegExp(`${regexBase}(?:/|$)`);
  }
  return new RegExp(`${regexBase}${PATH_END_REGEX_STRING}`);
};

const NO_REWRITE_PREFIXES = ["/org-", "/clear-cache", "/maintenance", "/sign-in", "/sign-up", "/switch-org"];
export const useOrgRouter = () => {
  const router = useRouter();
  const orgSlug = router.query.orgSlug as string | undefined;
  const makeOrgPath = <T extends string | UrlObject>(href: T): T => {
    if (typeof href === "object") {
      const pathname = href.pathname;
      return pathname ? { ...href, pathname: makeOrgPath(pathname) } : href;
    }
    if (!orgSlug || !href.startsWith("/") || NO_REWRITE_PREFIXES.some(prefix => href.startsWith(prefix))) {
      return href;
    }
    return `/${orgSlug}${href}` as T;
  };
  const pathnameWithoutOrg = router.pathname.split("/[orgSlug]/").pop();
  return {
    ...router,
    pathnameWithoutOrg,
    makeOrgPath,
    push: (href: string | UrlObject) => router.push(makeOrgPath(href)),
    replace: (href: string | UrlObject) => router.replace(makeOrgPath(href)),
  };
};

/**
 * Determines if a page should redirect to 404 based on invalid ID or NOT_FOUND error
 * Used by detail layout pages to handle invalid UUIDs and entity not found scenarios
 *
 * @param id - The entity ID from the URL
 * @param error - The tRPC error object
 * @returns true if should redirect to 404, false otherwise
 */
export const shouldRedirectToNotFound = (id: string | undefined, error: TRPCClientErrorLike<AppRouter> | null): boolean => {
  return !isUuid(id) || error?.data?.code === "NOT_FOUND";
};
